import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageHeader,
  Card,
  Badge,
  Tabs,
  KeyValue,
  Table,
  Spinner,
  SectionHeader,
  Button,
  Input,
  Select,
  Toggle,
  Modal,
  ProgressBar,
  QueryError,
} from "../components";
import { OpenHankAsAdminButton } from "../components/OpenHankAsAdminButton";
import { usePolling } from "../hooks/usePolling";
import { apiGet, apiPatch, apiPost, apiDelete } from "../lib/api";
import { useToast } from "../contexts/ToastContext";
import {
  buildOverridePatchBody,
  getPluginOverrideCandidates,
  resolvePluginOverrideState,
  type PluginOverrideState,
} from "../lib/pluginOverrides";
// Bucket C contract — single source of truth for the tenant detail wire
// shape. Type-only import: Vite never resolves zod at build time, and
// adam/package.json now lists zod as a runtime dep so the contract module
// type-checks under Adam's tsconfig.
import type {
  TenantDetailResponse,
  TenantPatchRequest,
  TenantPatchResponse,
} from "../../../../src/shared/contracts/tenants.js";
import { computeEffectiveTenantStatus } from "./tenantStatusRollup";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Bucket C R3 follow-up — local `TenantDetailData`, `BillingEntry`,
// `TenantAiContract`, `ActivityEntry`, `UpgradeHistoryEntry`, `TenantConfig`,
// and `Version` interfaces have been deleted. The wire shape is now
// sourced from `TenantDetailResponse` in
// `src/shared/contracts/tenants.ts`; per-row shapes are reached via
// indexed access where needed. See commit message for rationale.

// Row-shape aliases used by render-site closures — both derived from the
// typed contract so they move with the wire shape.
type ActivityEntry = TenantDetailResponse["recentActivity"][number];

interface TenantEmail {
  id: string;
  to: string;
  subject: string;
  template: string;
  status: "delivered" | "bounced" | "failed" | "sent" | "queued";
  sentAt: string;
}

// Version registry row used by the upgrade modal. Independent of the
// tenant-detail wire shape.
interface Version {
  id: string;
  imageTag: string;
  commitSha: string | null;
  releaseNotes: string | null;
  isStable: boolean;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TIER_BADGE_COLOR: Record<string, "blue" | "gray" | "secondary"> = {
  enterprise: "blue",
  pro: "gray",
  starter: "secondary",
};

const STATUS_BADGE_COLOR: Record<string, "green" | "red" | "blue" | "secondary"> = {
  active: "green",
  suspended: "red",
  provisioning: "blue",
  failed: "red",
  deprovisioned: "secondary",
  // F-011 (session 3): the rollup helper below may surface deployment.status
  // values directly (e.g. when a deployment is "deprovisioned" but tenant
  // is still "active"). Make sure all DeploymentStatus values map to a color.
  ready: "green",
  paused: "secondary",
  upgrading: "blue",
  migrating: "blue",
  archived: "secondary",
};

// F-011 (session 3, 2026-05-11) — computeEffectiveTenantStatus extracted to
// ./tenantStatusRollup.ts so it can be unit-tested without React.

const PLAN_LIMITS: Record<string, { computeMinutes: number; price: number }> = {
  starter: { computeMinutes: 720, price: 29 },
  pro: { computeMinutes: 4320, price: 99 },
  enterprise: { computeMinutes: -1, price: -1 },
};

const FEATURE_LABELS: Record<string, string> = {
  optimizer: "Process Optimizer",
  operatorModel: "Operator Model",
  canvas: "Dynamic Canvas",
  memoryPrimer: "Memory Primer",
  workspaceZones: "Workspace Zones",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState("overview");

  // Editing state for configuration tab
  const [editing, setEditing] = useState(false);
  const [editConfig, setEditConfig] = useState<TenantDetailResponse["config"] | null>(null);

  // Modal states
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeTier, setUpgradeTier] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [versionUpgradeOpen, setVersionUpgradeOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [rollbackOpen, setRollbackOpen] = useState(false);

  // ---------- Queries & Mutations ----------

  const {
    data: tenant,
    isLoading,
    error,
  } = useQuery<TenantDetailResponse>({
    queryKey: ["tenants", id],
    // Bucket C R3 — wire shape typed against TenantDetailResponse.
    queryFn: () => apiGet<TenantDetailResponse>(`/api/tenants/${id}`),
    enabled: !!id,
  });

  usePolling([["tenants", id ?? ""]], 30_000);

  // Bucket C: PATCH body typed against the contract. Adam's UI bundles only
  // the type (Vite never imports zod at build), so the dependency is
  // type-only here even though zod is a runtime dep of the contract module
  // itself.
  const patchMutation = useMutation<TenantPatchResponse, Error, TenantPatchRequest>({
    mutationFn: (body) => apiPatch<TenantPatchResponse>(`/api/tenants/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants", id] });
      setEditing(false);
      setEditConfig(null);
      toast.success("Configuration saved");
    },
    onError: (err: Error) => toast.error("Failed to save configuration", err.message),
  });

  const upgradeMutation = useMutation({
    mutationFn: (tier: string) => apiPatch(`/api/tenants/${id}`, { tier }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants", id] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setUpgradeModalOpen(false);
      setUpgradeTier("");
      toast.success("Tier changed successfully");
    },
    onError: (err: Error) => toast.error("Failed to change tier", err.message),
  });

  const suspendMutation = useMutation({
    mutationFn: (action: "suspend" | "resume") => apiPost(`/api/tenants/${id}/${action}`),
    onSuccess: (_data, action) => {
      queryClient.invalidateQueries({ queryKey: ["tenants", id] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast.success(action === "suspend" ? "Tenant suspended" : "Tenant resumed");
    },
    onError: (err: Error) => toast.error("Action failed", err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(`/api/tenants/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast.success("Tenant deleted");
      navigate("/tenants");
    },
    onError: (err: Error) => toast.error("Failed to delete tenant", err.message),
  });

  // Fetch available versions for upgrade selector
  const { data: versionsData } = useQuery<{ versions: Version[]; total: number }>({
    queryKey: ["versions"],
    queryFn: () => apiGet<{ versions: Version[]; total: number }>("/api/versions"),
    enabled: versionUpgradeOpen,
  });
  const stableVersions = (versionsData?.versions ?? []).filter((v) => v.isStable);

  const { data: tenantEmails } = useQuery<TenantEmail[]>({
    queryKey: ["tenants", id, "emails"],
    queryFn: () => apiGet<TenantEmail[]>(`/api/tenants/${id}/emails`),
    enabled: !!id && activeTab === "emails",
  });

  const versionUpgradeMutation = useMutation({
    mutationFn: (imageTag: string) => apiPost(`/api/tenants/${id}/upgrade`, { imageTag }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants", id] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setVersionUpgradeOpen(false);
      setSelectedVersion("");
      toast.success("Version upgrade started");
    },
    onError: (err: Error) => toast.error("Upgrade failed", err.message),
  });

  const rollbackMutation = useMutation({
    mutationFn: () => apiPost(`/api/tenants/${id}/rollback`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants", id] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setRollbackOpen(false);
      toast.success("Rollback initiated");
    },
    onError: (err: Error) => toast.error("Rollback failed", err.message),
  });

  // Plugin override mutation (BUCKET G.6) — one PATCH per tri-state change.
  const [pendingPluginId, setPendingPluginId] = useState<string | null>(null);
  const [pluginOverrideError, setPluginOverrideError] = useState<string | null>(null);
  const pluginOverrideMutation = useMutation({
    mutationFn: (args: { pluginId: string; nextState: PluginOverrideState }) => {
      if (!tenant) throw new Error("Tenant not loaded");
      const body = buildOverridePatchBody(tenant.config, args.pluginId, args.nextState);
      return apiPatch(`/api/tenants/${id}`, body);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["tenants", id] });
      setPendingPluginId(null);
      setPluginOverrideError(null);
      toast.success(`Plugin override updated (${vars.pluginId})`);
    },
    onError: (err: Error) => {
      setPendingPluginId(null);
      setPluginOverrideError(err.message);
    },
  });

  // ---------- Helpers ----------

  function startEditing() {
    if (!tenant || !tenant.configurationWritable) return;
    setEditConfig(structuredClone(tenant.config));
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setEditConfig(null);
  }

  function saveConfig() {
    if (!editConfig) return;
    patchMutation.mutate({ config: editConfig });
  }

  function setEditFeature(key: string, value: boolean) {
    if (!editConfig) return;
    setEditConfig({
      ...editConfig,
      features: { ...editConfig.features, [key]: value },
    });
  }

  // ---------- Loading state ----------

  if (isLoading || (!tenant && !error)) {
    return (
      <div>
        <PageHeader title="Tenant Detail" subtitle="View tenant configuration and activity" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (error && !tenant) {
    return (
      <div>
        <PageHeader title="Tenant Detail" subtitle="View tenant configuration and activity" />
        <QueryError error={error} queryKeys={[["tenants", id ?? ""]]} />
      </div>
    );
  }

  // ---------- Tab definitions ----------

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "configuration", label: "Configuration" },
    { id: "activity", label: "Activity", count: tenant.recentActivity?.length },
    { id: "emails", label: "Emails", count: tenantEmails?.length },
  ];

  const activityColumns = [
    {
      key: "timestamp",
      header: "Timestamp",
      render: (row: ActivityEntry) => (
        <span className="font-mono text-xs" style={{ color: "var(--c-text)" }}>
          {new Date(row.timestamp).toLocaleString()}
        </span>
      ),
    },
    {
      key: "action",
      header: "Action",
      render: (row: ActivityEntry) => (
        <span className="font-medium" style={{ color: "var(--c-text)" }}>
          {row.action}
        </span>
      ),
    },
    { key: "details", header: "Details" },
  ];

  // Available tiers for upgrade (exclude current)
  const availableTiers = ["starter", "pro", "enterprise"].filter((t) => t !== tenant.tier);

  // F-011 (BugBot LOW round-2): compute rollup status ONCE per render and
  // share the result between the header badge + the action-bar gate. The
  // previous version called computeEffectiveTenantStatus twice inside
  // separate IIFEs (one per JSX section). Same arguments both times, same
  // result both times, but the redundancy invited drift if the inputs
  // ever changed unevenly. Hoisting also drops the two IIFEs.
  const effectiveTenantStatus = computeEffectiveTenantStatus(
    tenant.status,
    tenant.deployments ?? [],
  );
  const lifecycleActionsDisabledTitle = effectiveTenantStatus.downgraded
    ? `Lifecycle actions disabled — ${effectiveTenantStatus.reason}`
    : undefined;

  return (
    <div>
      {/* Back link */}
      <div className="mb-4">
        <Link to="/tenants" className="text-xs font-medium" style={{ color: "var(--c-primary)" }}>
          &larr; Back to Tenants
        </Link>
      </div>

      {/* Header with action buttons */}
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-headline" style={{ color: "var(--c-text)" }}>
          {tenant.name}
        </h1>
        <Badge color={TIER_BADGE_COLOR[tenant.tier]}>{tenant.tier}</Badge>
        {/* F-011: surface the rollup status (tenant + deployment). If any
            deployment is in a downgraded lifecycle state, show THAT instead
            of the business-level tenant.status — and explain the divergence. */}
        <Badge color={STATUS_BADGE_COLOR[effectiveTenantStatus.displayStatus] ?? "secondary"} dot>
          {effectiveTenantStatus.displayStatus}
        </Badge>
        {effectiveTenantStatus.downgraded && effectiveTenantStatus.reason && (
          <span
            className="text-xs"
            style={{ color: "var(--c-warning, #b8860b)" }}
            title="The deployment is in a state that overrides the tenant's business-level status. Lifecycle actions are disabled until the deployment recovers."
          >
            ({effectiveTenantStatus.reason})
          </span>
        )}
        <span className="text-sm ml-auto" style={{ color: "var(--c-text-secondary)" }}>
          {tenant.email}
        </span>
      </div>

      {/* Version info */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
          Version:
        </span>
        <code className="text-xs" style={{ color: "var(--c-text)" }}>
          {tenant.currentVersion}
        </code>
        {tenant.lastUpgradeAt && (
          <span className="text-xs" style={{ color: "var(--c-text-faint)" }}>
            upgraded {new Date(tenant.lastUpgradeAt).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Action bar — F-011: lifecycle actions are disabled when the effective
          rollup status is downgraded (deployment in deprovisioned/failed/archived
          state). Otherwise clicking Suspend on a deprovisioned deployment hits
          F-010 and the operator sees a confusing 500. */}
      {tenant.lifecycleActionsSupported ? (
        <div className="flex gap-2 mb-8">
          <Button
            size="sm"
            variant={tenant.status === "active" ? "danger" : "primary"}
            onClick={() =>
              suspendMutation.mutate(tenant.status === "active" ? "suspend" : "resume")
            }
            disabled={suspendMutation.isPending || effectiveTenantStatus.downgraded}
            title={lifecycleActionsDisabledTitle}
          >
            {tenant.status === "active" ? "Suspend" : "Resume"}
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => setVersionUpgradeOpen(true)}
            disabled={tenant.status !== "active" || effectiveTenantStatus.downgraded}
            title={lifecycleActionsDisabledTitle}
          >
            Upgrade Version
          </Button>
          {tenant.previousVersion && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setRollbackOpen(true)}
              disabled={tenant.status !== "active" || effectiveTenantStatus.downgraded}
              title={lifecycleActionsDisabledTitle}
            >
              Rollback to {tenant.previousVersion}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setUpgradeTier(availableTiers[availableTiers.length - 1] ?? "");
              setUpgradeModalOpen(true);
            }}
          >
            Change Tier
          </Button>
          <Button size="sm" variant="danger" onClick={() => setDeleteModalOpen(true)}>
            Delete
          </Button>
        </div>
      ) : (
        <Card className="mb-8">
          <div className="space-y-2 text-sm">
            <div className="font-medium" style={{ color: "var(--c-text)" }}>
              Lifecycle controls are hidden for externally managed lab tenants.
            </div>
            <div style={{ color: "var(--c-text-secondary)", lineHeight: 1.5 }}>
              This tenant is synced from the prod-mirror lab environment. Suspend, upgrade,
              rollback, and delete remain stocktaked in Adam until VM-first tenant management is
              wired through the operator plane.
            </div>
          </div>
        </Card>
      )}

      <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />

      {/* ── Overview Tab ──────────────────────────────────────────────── */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          <Card>
            <SectionHeader title="Tenant Info" />
            <KeyValue label="Monthly Spend" value={`$${tenant.monthlySpend.toLocaleString()}`} />
            <KeyValue label="Total Sessions" value={String(tenant.totalSessions)} />
            <KeyValue label="Deployment Count" value={String(tenant.deploymentCount)} />
            <KeyValue label="Created" value={new Date(tenant.createdAt).toLocaleDateString()} />
          </Card>

          <Card>
            <SectionHeader title="Economics" subtitle="Canonical control-plane view" />
            <KeyValue
              label="Revenue"
              value={
                tenant.economics.revenueUsd === null
                  ? "Incomplete"
                  : `$${tenant.economics.revenueUsd.toLocaleString()}`
              }
            />
            <KeyValue
              label="Fixed Infra Cost"
              value={`$${tenant.economics.fixedInfraCostUsd.toLocaleString()}`}
            />
            <KeyValue
              label="Platform Inference COGS"
              value={`$${tenant.economics.platformPaidInferenceCogsUsd.toLocaleString()}`}
            />
            <KeyValue
              label="Reference Spend"
              value={`$${tenant.economics.referenceSpendUsd.toLocaleString()}`}
            />
            <KeyValue
              label="Gross Margin"
              value={
                tenant.economics.grossMarginUsd === null
                  ? "Incomplete"
                  : `$${tenant.economics.grossMarginUsd.toLocaleString()}`
              }
            />
            <KeyValue
              label="Divergence Rate"
              value={`${(tenant.economics.divergenceRate * 100).toFixed(1)}%`}
            />
            <KeyValue
              label="Local Usage Share"
              value={`${(tenant.economics.localUsageShare * 100).toFixed(1)}%`}
            />
            <KeyValue
              label="Credits Remaining"
              value={
                tenant.economics.creditsRemainingUsd === -1
                  ? "Unlimited"
                  : tenant.economics.creditsRemainingUsd === null
                    ? "N/A"
                    : `$${tenant.economics.creditsRemainingUsd.toLocaleString()}`
              }
            />
          </Card>

          <Card>
            <SectionHeader title="Plan & Usage" />
            <KeyValue
              label="Plan"
              value={`${tenant.tier.charAt(0).toUpperCase() + tenant.tier.slice(1)} — $${PLAN_LIMITS[tenant.tier]?.price === -1 ? "Custom" : PLAN_LIMITS[tenant.tier]?.price + "/mo"}`}
            />
            {PLAN_LIMITS[tenant.tier]?.computeMinutes > 0 && (
              <div style={{ padding: "8px 0" }}>
                <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
                  <span className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
                    Compute usage this month
                  </span>
                  <span className="text-xs" style={{ color: "var(--c-text-faint)" }}>
                    {tenant.totalSessions} / {PLAN_LIMITS[tenant.tier].computeMinutes} min
                  </span>
                </div>
                <ProgressBar
                  value={tenant.totalSessions}
                  max={PLAN_LIMITS[tenant.tier].computeMinutes}
                />
              </div>
            )}
          </Card>

          <Card>
            <SectionHeader title="AI entitlement" subtitle="Pre-provision commercial defaults" />
            <KeyValue
              label="Platform inference"
              value={
                (tenant.commercial ?? tenant.config.commercial)?.platformInferenceIncluded
                  ? "Included"
                  : "BYO required"
              }
            />
            <KeyValue
              label="Default daily budget"
              value={
                (tenant.commercial ?? tenant.config.commercial)?.defaultDailyBudgetUsd == null
                  ? "Unset"
                  : `$${(tenant.commercial ?? tenant.config.commercial)?.defaultDailyBudgetUsd}`
              }
            />
            <KeyValue
              label="BYO subscription"
              value={
                (tenant.commercial ?? tenant.config.commercial)?.allowByoSubscription === false
                  ? "Disabled"
                  : "Allowed"
              }
            />
            <KeyValue
              label="BYO API"
              value={
                (tenant.commercial ?? tenant.config.commercial)?.allowByoApi === false
                  ? "Disabled"
                  : "Allowed"
              }
            />
            {tenant.ai && (
              <KeyValue
                label="Default Hank preference"
                value={
                  tenant.ai.preferredSource === "workspace_default"
                    ? "Workspace default"
                    : tenant.ai.preferredSource === "byo_subscription"
                      ? "BYO subscription"
                      : "BYO API"
                }
              />
            )}
          </Card>

          <Card>
            <SectionHeader title="Billing History" subtitle="Last 3 months" />
            {(tenant.billingHistory ?? []).slice(0, 3).map((entry) => (
              <div
                key={entry.month}
                className="flex items-center justify-between py-2"
                style={{ borderBottom: "1px solid var(--c-active)" }}
              >
                <span className="text-sm" style={{ color: "var(--c-text)" }}>
                  {entry.month}
                </span>
                <div className="flex items-center gap-4">
                  <span className="text-xs" style={{ color: "var(--c-text-faint)" }}>
                    {entry.episodes.toLocaleString()} episodes
                  </span>
                  <span className="text-sm font-medium" style={{ color: "var(--c-text)" }}>
                    ${entry.amount.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </Card>

          <Card>
            <SectionHeader title="Deployments" subtitle="Canonical Hank runtime endpoints" />
            {(tenant.deployments ?? []).map((deployment) => (
              <div
                key={deployment.id}
                className="py-3"
                style={{ borderBottom: "1px solid var(--c-active)" }}
              >
                <KeyValue label="Deployment" value={deployment.id} />
                <KeyValue label="Runtime Profile" value={deployment.runtime_profile} />
                <KeyValue label="Inference Mode" value={deployment.inference_mode} />
                <KeyValue label="Auth Mode" value={deployment.auth_mode} />
                <KeyValue label="Gateway URL" value={deployment.gateway_url} />
                <KeyValue label="Hank URL" value={deployment.hank_url} />
                <KeyValue label="Local Model Profile" value={deployment.local_model_profile} />
                <KeyValue
                  label="Management"
                  value={
                    deployment.management_mode === "external" ||
                    deployment.management_source === "external_sync" ||
                    deployment.management_source === "prod_mirror_lab"
                      ? "External deployment"
                      : "Managed"
                  }
                />
                {/* F-014: platform_admin per-deployment JWT mint. CP issues
                    a Hank-audience token scoped to this tenantId at
                    click-time, preserving Hank's tenant-resolver invariant. */}
                <div className="mt-3">
                  <OpenHankAsAdminButton tenantId={tenant.id} hankUrl={deployment.hank_url ?? ""} />
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* ── Configuration Tab (Editable) ─────────────────────────────── */}
      {activeTab === "configuration" && (
        <div className="space-y-6">
          {/* Edit/Save/Cancel controls */}
          <div className="flex justify-end gap-2">
            {tenant.configurationWritable ? (
              editing ? (
                <>
                  <Button size="sm" variant="ghost" onClick={cancelEditing}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={saveConfig}
                    disabled={patchMutation.isPending}
                  >
                    {patchMutation.isPending ? "Saving..." : "Save"}
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="ghost" onClick={startEditing}>
                  Edit
                </Button>
              )
            ) : (
              <span className="text-xs" style={{ color: "var(--c-text-faint)" }}>
                Configuration is read-only for externally managed lab tenants.
              </span>
            )}
          </div>

          <Card>
            <SectionHeader title="Limits" />
            {editing && editConfig ? (
              <div className="space-y-4">
                <Input
                  label="Max Sessions"
                  type="number"
                  value={String(editConfig.maxSessions)}
                  onChange={(e) =>
                    setEditConfig({ ...editConfig, maxSessions: parseInt(e.target.value, 10) || 0 })
                  }
                />
                <Input
                  label="Max Episodes / Day"
                  type="number"
                  value={String(editConfig.maxEpisodesPerDay)}
                  onChange={(e) =>
                    setEditConfig({
                      ...editConfig,
                      maxEpisodesPerDay: parseInt(e.target.value, 10) || 0,
                    })
                  }
                />
              </div>
            ) : (
              <>
                <KeyValue label="Max Sessions" value={String(tenant.config.maxSessions)} />
                <KeyValue
                  label="Max Episodes / Day"
                  value={String(tenant.config.maxEpisodesPerDay)}
                />
              </>
            )}
          </Card>

          <Card>
            <SectionHeader title="Allowed Models" />
            <div className="flex flex-wrap gap-2">
              {(editing && editConfig ? editConfig : tenant.config).allowedModels.map((model) => (
                <Badge key={model} color="blue">
                  {model}
                </Badge>
              ))}
            </div>
          </Card>

          <Card>
            <SectionHeader title="Feature Flags" />
            {Object.entries(
              editing && editConfig ? editConfig.features : tenant.config.features,
            ).map(([key, enabled]) => (
              <div
                key={key}
                className="flex items-center justify-between py-2"
                style={{ borderBottom: "1px solid var(--c-active)" }}
              >
                <span className="text-sm" style={{ color: "var(--c-text)" }}>
                  {FEATURE_LABELS[key] ?? key}
                </span>
                {editing ? (
                  <Toggle checked={enabled} onChange={(val) => setEditFeature(key, val)} />
                ) : (
                  <Badge color={enabled ? "green" : "gray"}>
                    {enabled ? "Enabled" : "Disabled"}
                  </Badge>
                )}
              </div>
            ))}
          </Card>

          {/* ── Plugin Overrides (BUCKET G.6) ──────────────────────────── */}
          <Card>
            <SectionHeader
              title="Plugin Overrides"
              subtitle="Force a plugin on or off for this tenant, overriding tier defaults."
            />
            {getPluginOverrideCandidates().map(({ pluginId, featureLabels }) => {
              const currentState = resolvePluginOverrideState(tenant.config, pluginId);
              const saving = pendingPluginId === pluginId && pluginOverrideMutation.isPending;
              const disabled = !tenant.configurationWritable || pluginOverrideMutation.isPending;
              return (
                <div
                  key={pluginId}
                  className="py-3"
                  style={{ borderBottom: "1px solid var(--c-active)" }}
                >
                  <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                    <div>
                      <div className="text-sm font-medium" style={{ color: "var(--c-text)" }}>
                        <code style={{ fontSize: 12 }}>{pluginId}</code>
                      </div>
                      <div className="text-xs" style={{ color: "var(--c-text-faint)" }}>
                        Backs: {featureLabels.join(", ")}
                      </div>
                    </div>
                    {saving && <Spinner size={14} />}
                  </div>
                  <div className="flex gap-2" role="radiogroup" aria-label={`${pluginId} override`}>
                    {(
                      [
                        { value: "default", label: "Default (tier)" },
                        { value: "on", label: "Force on" },
                        { value: "off", label: "Force off" },
                      ] as Array<{ value: PluginOverrideState; label: string }>
                    ).map((opt) => {
                      const selected = currentState === opt.value;
                      return (
                        <Button
                          key={opt.value}
                          size="sm"
                          variant={selected ? "primary" : "ghost"}
                          disabled={disabled || selected}
                          onClick={() => {
                            setPluginOverrideError(null);
                            setPendingPluginId(pluginId);
                            pluginOverrideMutation.mutate({
                              pluginId,
                              nextState: opt.value,
                            });
                          }}
                          aria-pressed={selected}
                          aria-label={`${pluginId} ${opt.label}`}
                        >
                          {opt.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {pluginOverrideError && (
              <div className="text-xs mt-3" style={{ color: "var(--c-danger)" }} role="alert">
                {pluginOverrideError}
              </div>
            )}
            {!tenant.configurationWritable && (
              <div className="text-xs mt-3" style={{ color: "var(--c-text-faint)" }}>
                Plugin overrides are read-only for externally managed lab tenants.
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ── Activity Tab ──────────────────────────────────────────────── */}
      {activeTab === "activity" && (
        <Card padding={false}>
          <Table
            columns={activityColumns}
            data={(tenant.recentActivity ?? []) as unknown as Record<string, unknown>[]}
            keyField="timestamp"
            emptyMessage="No recent activity"
          />
        </Card>
      )}

      {/* ── Emails Tab ────────────────────────────────────────────────── */}
      {activeTab === "emails" && (
        <Card padding={false}>
          <Table
            columns={[
              { key: "subject", header: "Subject" },
              {
                key: "template",
                header: "Template",
                render: (row: TenantEmail) => <Badge color="blue">{row.template}</Badge>,
              },
              {
                key: "status",
                header: "Status",
                render: (row: TenantEmail) => (
                  <Badge
                    color={
                      row.status === "delivered"
                        ? "green"
                        : row.status === "bounced"
                          ? "amber"
                          : row.status === "failed"
                            ? "red"
                            : "blue"
                    }
                  >
                    {row.status}
                  </Badge>
                ),
              },
              {
                key: "sentAt",
                header: "Sent",
                render: (row: TenantEmail) => (
                  <span className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
                    {new Date(row.sentAt).toLocaleString()}
                  </span>
                ),
              },
            ]}
            data={(tenantEmails ?? []) as unknown as Record<string, unknown>[]}
            keyField="id"
            emptyMessage="No emails sent to this tenant"
          />
        </Card>
      )}

      {/* ── Upgrade Modal ─────────────────────────────────────────────── */}
      <Modal open={upgradeModalOpen} onClose={() => setUpgradeModalOpen(false)} title="Change Tier">
        <div className="space-y-4">
          <p className="text-sm" style={{ color: "var(--c-text-secondary)" }}>
            Current tier: <strong>{tenant.tier}</strong>. Select the new tier:
          </p>
          <Select
            label="New Tier"
            value={upgradeTier}
            onChange={(e) => setUpgradeTier(e.target.value)}
            options={availableTiers.map((t) => ({
              value: t,
              label: t.charAt(0).toUpperCase() + t.slice(1),
            }))}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setUpgradeModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => upgradeMutation.mutate(upgradeTier)}
              disabled={!upgradeTier || upgradeMutation.isPending}
            >
              {upgradeMutation.isPending ? "Upgrading..." : "Confirm"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Delete Modal ──────────────────────────────────────────────── */}
      <Modal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteConfirm("");
        }}
        title="Delete Tenant"
      >
        <div className="space-y-4">
          <p className="text-sm" style={{ color: "var(--c-danger)" }}>
            This will permanently deprovision <strong>{tenant.name}</strong> and delete all
            associated deployments.
          </p>
          <Input
            label={`Type "${tenant.name}" to confirm`}
            placeholder={tenant.name}
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => {
                setDeleteModalOpen(false);
                setDeleteConfirm("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteConfirm !== tenant.name || deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Permanently"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Version Upgrade Modal ──────────────────────────────────────── */}
      <Modal
        open={versionUpgradeOpen}
        onClose={() => {
          setVersionUpgradeOpen(false);
          setSelectedVersion("");
        }}
        title="Upgrade Version"
      >
        <div className="space-y-4">
          <p className="text-sm" style={{ color: "var(--c-text-secondary)" }}>
            Current version: <code style={{ fontSize: 12 }}>{tenant.currentVersion}</code>
          </p>
          {stableVersions.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--c-text-faint)" }}>
              No stable versions available. Register a stable version on the Versions page first.
            </p>
          ) : (
            <Select
              label="Target Version"
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e.target.value)}
              options={[
                { value: "", label: "Select a version..." },
                ...stableVersions
                  .filter((v) => v.imageTag !== tenant.currentVersion)
                  .map((v) => ({
                    value: v.imageTag,
                    label: `${v.imageTag}${v.releaseNotes ? ` — ${v.releaseNotes}` : ""}`,
                  })),
              ]}
            />
          )}
          {selectedVersion && (
            <div
              className="text-xs p-3 rounded"
              style={{ backgroundColor: "var(--c-active)", color: "var(--c-text-secondary)" }}
            >
              This will upgrade <strong>{tenant.name}</strong> from{" "}
              <code>{tenant.currentVersion}</code> to <code>{selectedVersion}</code>. The current
              version will be preserved for rollback.
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => {
                setVersionUpgradeOpen(false);
                setSelectedVersion("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => versionUpgradeMutation.mutate(selectedVersion)}
              disabled={!selectedVersion || versionUpgradeMutation.isPending}
            >
              {versionUpgradeMutation.isPending ? "Upgrading..." : "Confirm Upgrade"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Rollback Confirmation Modal ────────────────────────────────── */}
      <Modal open={rollbackOpen} onClose={() => setRollbackOpen(false)} title="Confirm Rollback">
        <div className="space-y-4">
          <div
            className="text-sm p-3 rounded"
            style={{
              backgroundColor: "rgba(var(--c-warning-rgb, 245, 158, 11), 0.1)",
              border: "1px solid var(--c-warning)",
            }}
          >
            <p style={{ color: "var(--c-text)", margin: 0 }}>
              This will roll back <strong>{tenant.name}</strong> from{" "}
              <code>{tenant.currentVersion}</code> to <code>{tenant.previousVersion}</code>.
            </p>
          </div>
          <p className="text-xs" style={{ color: "var(--c-text-faint)" }}>
            The current version will be preserved so you can upgrade again later.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setRollbackOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => rollbackMutation.mutate()}
              disabled={rollbackMutation.isPending}
            >
              {rollbackMutation.isPending ? "Rolling back..." : "Confirm Rollback"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
